import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowLeft, 
  Calendar, 
  Users, 
  Clock, 
  Tag,
  AlertCircle,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { getTagColors } from '@/lib/tag-colors';
import { getStatusColors } from '@/lib/status-colors';
import { format } from 'date-fns';
import { fetchTaskContent } from '@/lib/notion-client';
import { useOptionalProductionManagement } from '@/lib/production-management-context';
import { CommentsSection } from '@/components/comments-section';
import { UserAvatar } from '@/components/user-avatar';
import MarkdownContent from '@/components/markdown-content';
import type { Task } from '@/lib/types';

export default function TaskPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const productionContext = useOptionalProductionManagement();
  const resources = productionContext?.resources ?? [];
  const getUsersByEmails = productionContext?.getUsersByEmails;
  
  // Get task data from navigation state if available
  const taskFromState = location.state?.task as Task | undefined;
  
  const wpConfig = (window as any).WP_COCKPIT
  const isDev = wpConfig?.devMode || false
  
  if (isDev && taskFromState) {
    console.log('[TaskPage] Task from navigation state:', taskFromState)
    console.log('[TaskPage] Task assignee field:', taskFromState.assignee)
  }
  
  const [task, setTask] = useState<Task | null>(taskFromState || null);
  const [taskContent, setTaskContent] = useState<string>('');
  const [loading, setLoading] = useState(!taskFromState);
  const [error, setError] = useState<string | null>(null);

  // Helper function to get contributor names from IDs
  const getContributorNames = (contributorIds?: string[]): string => {
    const wp = (window as any).WP_COCKPIT
    const isDev = wp?.devMode || false
    
    if (isDev) {
      console.log('[TaskPage] getContributorNames called with:', contributorIds)
      console.log('[TaskPage] Task data:', task)
      console.log('[TaskPage] Available resources:', resources.length)
    }
    
    if (!contributorIds || contributorIds.length === 0) return 'None'
    
    const names = contributorIds
      .map(id => {
        const resource = resources.find(r => r.id === id)
        return resource?.name?.trim() || null
      })
      .filter(Boolean) as string[]
    
    // If we have matched names, show them. Otherwise show count
    if (names.length > 0) {
      return names.join(', ')
    }
    
    // Show count of contributors instead of IDs
    return `${contributorIds.length} contributor${contributorIds.length !== 1 ? 's' : ''}`
  }

  const fetchTask = async (force: boolean = false) => {
    if (!taskId) return;
    
    try {
      setError(null);
      
      const wp = (window as any).WP_COCKPIT;
      const isDev = wp?.devMode || false;
      
      if (isDev) {
        console.log('[fetchTask] Fetching task content for:', taskId);
        console.log('[fetchTask] Force refresh:', force);
        console.log('[fetchTask] Task from state:', taskFromState);
      }
      
      // If we have task data from state, use it
      if (taskFromState && !task) {
        setTask(taskFromState);
      }
      
      const data = await fetchTaskContent(taskId, force);
      
      if (isDev) console.log('[fetchTask] Response data:', data);
      
      if (!data) {
        if (!taskFromState) {
          setError('Task content not found');
        }
        return;
      }
      
      if (data.content) {
        setTaskContent(data.content);
      } else {
        setTaskContent('');
      }
      
      // If we don't have task metadata from state, show error
      if (!taskFromState && !task) {
        setError('Task metadata not available. Please navigate from the tasks list.');
      }
    } catch (err) {
      console.error('Error fetching task:', err);
      if (!taskFromState) {
        setError('Failed to load task content');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTask();
  }, [taskId]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-6"></div>
            <div className="grid gap-6 md:grid-cols-2">
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error || 'Task not found'}</AlertDescription>
          </Alert>
          <Button 
            onClick={() => navigate(-1)} 
            variant="outline" 
            className="mt-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (isDev) {
    console.log('[TaskPage RENDER] Current task object:', task)
    console.log('[TaskPage RENDER] task.assignee:', task.assignee)
    console.log('[TaskPage RENDER] task.assignee exists?', !!task.assignee)
    console.log('[TaskPage RENDER] task.assignee length:', task.assignee?.length)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            onClick={() => navigate(-1)} 
            variant="outline" 
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {task.title}
                </h1>
                <Badge className={`${getStatusColors(task.status).bg} ${getStatusColors(task.status).text} ${getStatusColors(task.status).border} border`}>
                  {task.status}
                </Badge>
              </div>
              {task.description && task.description !== "Loading..." && (
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  {task.description}
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {task.url && (
                <Button
                  onClick={() => window.open(task.url, '_blank')}
                  variant="outline"
                  size="sm"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in Notion
                </Button>
              )}
              <Button onClick={() => fetchTask(true)}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Task Details */}
            <Card>
              <CardHeader>
                <CardTitle>Task Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {task.due && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Due Date:</span>
                      <span className="text-sm">{formatDate(task.due?.toString())}</span>
                    </div>
                  )}
                  
                  {task.estimatedHours !== undefined && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Estimated:</span>
                      <span className="text-sm">{task.estimatedHours}h</span>
                    </div>
                  )}

                  {task.realisedHours !== undefined && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-muted-foreground">Tracked:</span>
                      <span className="text-sm font-medium">{task.realisedHours.toFixed(1)}h</span>
                      {task.estimatedHours !== undefined && (
                        <span className="text-xs text-muted-foreground">
                          ({((task.realisedHours / task.estimatedHours) * 100).toFixed(0)}%)
                        </span>
                      )}
                    </div>
                  )}

                  {task.assignee && task.assignee.length > 0 && (() => {
                    const assigneeUsers = getUsersByEmails ? getUsersByEmails(task.assignee) : []
                    return (
                      <div className="flex items-center gap-2 md:col-span-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Assigned to:</span>
                        <div className="flex flex-wrap items-center gap-2">
                          {assigneeUsers.length > 0 ? (
                            <>
                              {assigneeUsers.map(user => (
                                <div key={user.id} className="flex items-center gap-1.5">
                                  <UserAvatar 
                                    displayName={user.name}
                                    profilePicture={user.profileImage}
                                    className="h-6 w-6"
                                  />
                                  <span className="text-sm font-medium">{user.name}</span>
                                </div>
                              ))}
                              {task.assignee.length > assigneeUsers.length && (
                                <span className="text-sm text-muted-foreground">
                                  +{task.assignee.length - assigneeUsers.length} more
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-sm font-medium">
                              {task.assignee.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })()}

                  {task.contributors && task.contributors.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Contributors:</span>
                      <span className="text-sm">{getContributorNames(task.contributors)}</span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {task.tags && task.tags.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Tags:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {task.tags.map((tag, index) => {
                        const tagColors = getTagColors(tag);
                        return (
                          <Badge key={index} className={`text-xs ${tagColors.bg} ${tagColors.text} ${tagColors.border} border`}>
                            {tag}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Task Content */}
            {taskContent && (
              <Card>
                <CardHeader>
                  <CardTitle>Content</CardTitle>
                </CardHeader>
                <CardContent>
                  <MarkdownContent content={taskContent} />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Task Status */}
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge className={`${getStatusColors(task.status).bg} ${getStatusColors(task.status).text} ${getStatusColors(task.status).border} border`}>
                  {task.status}
                </Badge>
              </CardContent>
            </Card>

            {/* Comments Section */}
            <CommentsSection id={task.id} type="task" />
          </div>
        </div>
      </div>
    </div>
  );
}